// assets/cart-drawer.js — OcuTrap Cart Drawer
(function () {
  'use strict';

  var drawer, overlay, closeBtn, cartBody, drawerPanel;

  // Section Rendering API id of this drawer (theme.liquid renders it with
  // {% section 'cart-drawer' %}). Passing it as `sections` to the Cart API
  // returns the re-rendered drawer HTML in the same response, so a quantity
  // change is ONE round-trip (was three: /cart.js, /cart/change.js, /cart?section_id).
  var SECTION_ID = 'cart-drawer';

  // Per-line optimistic state while a change is in flight, keyed by the line's
  // 1-based cart index: { qty: last painted quantity, inflight: bool,
  // next: qty queued behind the in-flight request or null }. Rapid changes
  // paint instantly and coalesce into at most one trailing request.
  //
  // Lines are addressed by INDEX, not item.key: Shopify regenerates the line
  // key on every /cart/change.js, so a follow-up request built from the
  // previously rendered key fails with 400 "no valid id or line parameter"
  // (proved 2026-08-23). The index is stable across a quantity change.
  var pendingQty = {};
  var otherMutation = false;
  var cartUnverified = false;
  var cartNotice = '';
  var verifiedCart = null;

  var SECTION_SWAP_SELECTORS = [
    '.cart-drawer__items',
    '.cart-drawer__upsell',
    '.cart-drawer__footer'
  ];

  // ---- Drawer interaction telemetry (REQ-ANALYTICS-PH-02) ----
  // One generic DOM event; snippets/ocutrap-analytics.liquid owns the dataLayer
  // mapping so this file stays cart logic. Adds NO network request: cart value
  // and item count are read from the drawer DOM the server already rendered.
  var openedAt = 0;

  function emit(name, detail) {
    try { document.dispatchEvent(new CustomEvent('ocu:cart-event', { detail: Object.assign({ name: name }, detail || {}) })); } catch (e) {}
  }

  // #CartSubtotal is server-rendered money ("$1,299.00"). This store prices in
  // USD, so stripping everything but digits and the decimal point is safe.
  function cartValueFromDom() {
    var el = document.getElementById('CartSubtotal');
    if (!el) return null;
    var n = parseFloat(String(el.textContent).replace(/[^0-9.]/g, ''));
    return isNaN(n) ? null : n;
  }

  function itemCountFromDom() {
    var el = document.getElementById('CartDrawerCountBadge') ||
             document.querySelector('.ocutrap-header__cart-count');
    if (!el) return 0;
    var n = parseInt(String(el.textContent).replace(/[^0-9]/g, ''), 10);
    return isNaN(n) ? 0 : n;
  }

  // Cart totals as the drawer currently shows them. The cart JSON is not in
  // scope in the interaction handlers and fetching it would add a request.
  function cartContext() {
    var value = cartValueFromDom();
    var ctx = { item_count: itemCountFromDom() };
    if (value !== null) ctx.cart_value = value;
    return ctx;
  }

  function lineItemId(row) {
    if (!row || !row.dataset) return '';
    return row.dataset.sku || row.dataset.variantId || '';
  }

  // Value of one add: /cart/add.js returns the added line (form post) or
  // { items: [...] } (JSON post). price is per unit in cents.
  function addedValue(added, quantity) {
    var line = (added && added.items && added.items[0]) || added || {};
    if (line.price != null) return (line.price * quantity) / 100;
    if (line.final_line_price != null) return line.final_line_price / 100;
    return null;
  }

  function init() {
    drawer      = document.getElementById('CartDrawer');
    overlay     = document.getElementById('CartDrawerOverlay');
    closeBtn    = document.getElementById('CartDrawerClose');
    cartBody    = document.getElementById('CartDrawerBody');
    drawerPanel = drawer && drawer.querySelector('.cart-drawer__panel');

    if (!drawer) return;

    document.querySelectorAll('[data-open-cart]').forEach(function (btn) {
      btn.addEventListener('click', function (e) { e.preventDefault(); openDrawer(e.currentTarget); });
    });

    // Wrapped so closeDrawer gets the trigger name, not the DOM event.
    if (closeBtn) closeBtn.addEventListener('click', function () { closeDrawer('button'); });
    if (overlay)  overlay.addEventListener('click', function () { closeDrawer('overlay'); });
    // Only an open drawer reacts: closeDrawer clears body overflow, and an
    // Escape meant for the mobile menu or another dialog must not unlock the
    // page scroll underneath it.
    document.addEventListener('keydown', function (e) {
      if (!isOpen()) return;
      if (e.key === 'Escape') closeDrawer('escape');
      else if (e.key === 'Tab') trapTab(e);
    });

    document.querySelectorAll('[data-type="add-to-cart-form"]').forEach(function (form) {
      form.addEventListener('submit', handleAddToCart);
    });

    if (cartBody) cartBody.addEventListener('click', handleBodyClick);
    if (cartBody) cartBody.addEventListener('submit', handleUpsellAdd);
    // Quantity is a <select>; 'change' is delegated because the drawer body is
    // replaced wholesale on every section swap.
    if (cartBody) cartBody.addEventListener('change', handleQtySelect);
    document.addEventListener('click', handleTierAdd);
    drawer.addEventListener('click', function (e) {
      if (e.target.closest('[data-ocu-track="cart_drawer_checkout"]') && (isCartBusy() || cartUnverified)) {
        e.preventDefault();
        e.stopPropagation();
      }
    });
  }

  function openDrawer(opener) {
    if (isCartBusy()) { showDrawer('cart_drawer', opener); return; }
    otherMutation = true;
    verifiedCart = null;
    syncPendingUi();
    fetchCart().then(renderCart).then(function () {
      cartUnverified = false;
      cartNotice = '';
    }).catch(function () {
      cartUnverified = true;
      cartNotice = 'We couldn’t refresh your cart. Review the full cart before checkout.';
    }).finally(function () {
      otherMutation = false;
      syncPendingUi();
      showDrawer('cart_drawer', opener);
    });
  }

  function isOpen() {
    return !!drawer && drawer.classList.contains('is-open');
  }

  // ---- Focus (the panel is role="dialog" aria-modal="true") ----
  // Opening moves focus into the panel, Tab cycles inside it, and closing
  // hands focus back to whatever opened it (header cart, PDP buy button,
  // sticky bar). Before this, focus stayed on the page behind the drawer, so
  // keyboard and screen-reader users tabbed through hidden page content.
  var returnFocusTo = null;
  var FOCUSABLE = 'a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]):not([type="hidden"]), summary, [tabindex]:not([tabindex="-1"])';

  function focusables() {
    if (!drawerPanel) return [];
    return Array.prototype.filter.call(drawerPanel.querySelectorAll(FOCUSABLE), function (el) {
      return !el.closest('[hidden]') && el.getClientRects().length > 0;
    });
  }

  function focusEl(el) {
    if (!el || typeof el.focus !== 'function') return;
    try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
  }

  function trapTab(e) {
    var items = focusables();
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;
    if (!drawerPanel.contains(active)) {
      e.preventDefault();
      focusEl(e.shiftKey ? last : first);
    } else if (e.shiftKey && active === first) {
      e.preventDefault();
      focusEl(last);
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      focusEl(first);
    }
  }

  // `opener` is the control that started an add. Callers capture it before
  // disabling that control, because a disabled button can lose focus.
  function showDrawer(source, opener) {
    var alreadyOpen = isOpen();
    if (!alreadyOpen) {
      var active = opener || document.activeElement;
      returnFocusTo = active && active !== document.body && !drawer.contains(active) ? active : null;
    }
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.inert = false;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('ocu-drawer-open');
    if (!alreadyOpen || !drawerPanel.contains(document.activeElement)) focusEl(closeBtn || focusables()[0]);
    openedAt = Date.now();
    document.dispatchEvent(new CustomEvent('cart:opened', { detail: { source: source, cart: cartUnverified ? null : verifiedCart } }));
  }

  // `source` is the trigger name ('button' | 'overlay' | 'escape'), not an
  // event: the three listeners pass it. Checkout is a plain <a href="/checkout">
  // so navigating away never calls this, and no close event is emitted for it.
  function closeDrawer(source) {
    var wasOpen = isOpen();
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.inert = true;
    document.body.style.overflow = '';
    document.body.classList.remove('ocu-drawer-open');
    if (!wasOpen) return;
    var target = returnFocusTo;
    function visibleControl(el) {
      return el && document.contains(el) && !el.disabled && !el.closest('[hidden], [inert]') && el.getClientRects().length > 0;
    }
    if (!visibleControl(target)) {
      // A prepaid purchase may replace the buy button with cart guidance.
      var product = target && target.closest('[data-plan-product]');
      target = product && Array.from(product.querySelectorAll('[data-cart-focus-fallback], [type="submit"]')).find(visibleControl);
      if (!target) target = Array.from(document.querySelectorAll('[data-open-cart]')).find(visibleControl);
    }
    focusEl(target);
    returnFocusTo = null;
    emit('cart_drawer_close', Object.assign({
      seconds_open: openedAt ? Math.round((Date.now() - openedAt) / 1000) : 0,
      closed_via: typeof source === 'string' ? source : 'other'
    }, cartContext()));
    openedAt = 0;
  }

  async function fetchCart() {
    const res = await fetch('/cart.js');
    if (!res.ok) throw new Error('Failed to fetch cart');
    return res.json();
  }

  // A submitted form is intent, not proof of an add. Use the returned product
  // identity, but the requested quantity (the response can be a merged line).
  function confirmAdded(added, quantity) {
    var item = (added && added.items && added.items[0]) || added;
    if (!item) return;
    document.dispatchEvent(new CustomEvent('cart:item-added', {
      detail: Object.assign({}, item, { quantity: quantity })
    }));
  }

  async function handleAddToCart(e) {
    e.preventDefault();
    if (cartUnverified) { showDrawer('cart_drawer'); return; }
    if (isCartBusy()) return;
    otherMutation = true;
    verifiedCart = null;
    cartNotice = '';
    syncPendingUi();
    const form = e.currentTarget;
    // Safari does not focus buttons on pointer activation. Prefer the actual
    // submitter, except when the PDP's external sticky control initiated it.
    const active = document.activeElement;
    const opener = active && active.matches('[data-cart-submit-for]') ? active : (e.submitter || active);
    // Busy state goes on the control that was pressed: the submitter (the
    // preorder app's visible clone, or the one the PDP sticky bar passes to
    // requestSubmit), plus any outside control marked data-cart-submit-for
    // this form. The first submit button alone was the wrong target on the
    // PDP: the app parks #PdpAddBtn at scale(0), so the pressed button never
    // showed it was working.
    // getAttribute, not form.id: a product form's <input name="id"> shadows it.
    const formId = form.getAttribute('id');
    const submitter = e.submitter && form.contains(e.submitter) ? e.submitter : form.querySelector('[type="submit"]');
    const busy = [submitter].concat(formId ? Array.from(document.querySelectorAll('[data-cart-submit-for="' + formId + '"]')) : [])
      .filter(Boolean)
      .map(function (el) { return { el: el, wasDisabled: el.disabled }; });
    busy.forEach(function (b) {
      b.el.classList.add('btn--loading');
      b.el.setAttribute('aria-busy', 'true');
      b.el.disabled = true;
    });
    var companionRejected = false;
    try {
      // Optional companion line (PDP prepaid-plan addon): ocutrap-pdp.js mirrors
      // the checkbox + pack quantity onto the form as data attributes. The
      // drawer HTML is requested from whichever add is last.
      const companionId = form.getAttribute('data-companion-id');
      // Snapshot both parts of this submission before yielding: pack controls
      // remain usable while the hardware request is in flight.
      const companionQty = parseInt(form.getAttribute('data-companion-qty'), 10) || 1;
      const fd = new FormData(form);
      if (!companionId) fd.append('sections', SECTION_ID);
      const res = await fetch('/cart/add.js', { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Add to cart failed');
      let added = await res.json();
      confirmAdded(added, parseInt(fd.get('quantity'), 10) || 1);
      if (companionId) {
        const companionRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(companionId, 10), quantity: companionQty, sections: SECTION_ID }),
        });
        if (!companionRes.ok) {
          // A 422 can still add the available stock. Refresh the cart
          // and offer only its missing plan balance, never replay this add.
          companionRejected = companionRes.status === 422;
          throw new Error('Add to cart failed');
        }
        added = await companionRes.json();
        confirmAdded(added, companionQty);
      }
      const cart = await fetchCart();
      cart.sections = added.sections;
      await renderCart(cart);
      showDrawer('cart_drawer', opener);
    } catch (error) {
      await recoverCart();
      if (companionRejected && !cartUnverified) {
        cartNotice = 'Your traps were added. ';
        cartNotice += drawerPanel.querySelector('.cart-upsell--bundle button')
          ? 'Use the plan option in your cart to add the missing prepaid plans without adding more traps.'
          : 'Review your cart before trying again.';
      }
      showDrawer('cart_drawer', opener);
    } finally {
      otherMutation = false;
      syncPendingUi();
      busy.forEach(function (b) {
        b.el.classList.remove('btn--loading');
        b.el.removeAttribute('aria-busy');
        b.el.disabled = b.wasDisabled;
      });
    }
  }

  async function handleUpsellAdd(e) {
    var form = e.target.closest('[data-cart-upsell-add]');
    if (!form) return;
    e.preventDefault();
    if (isCartBusy() || cartUnverified) return;
    otherMutation = true;
    verifiedCart = null;
    cartNotice = '';
    syncPendingUi();
    var btn = form.querySelector('[type="submit"]');
    if (btn) {
      btn.classList.add('btn--loading');
      btn.disabled = true;
    }
    try {
      var fd = new FormData(form);
      fd.append('sections', SECTION_ID);
      var res = await fetch('/cart/add.js', { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Add to cart failed');
      var added = await res.json();
      confirmAdded(added, parseInt(fd.get('quantity'), 10) || 1);
      var cart = await fetchCart();
      cart.sections = added.sections;
      await renderCart(cart);
      var tracked = form.closest('[data-ocu-track]');
      var trackName = tracked ? tracked.getAttribute('data-ocu-track') : '';
      var upsellQty = parseInt(fd.get('quantity'), 10) || 1;
      emit('cart_upsell_add', Object.assign({
        upsell: trackName === 'cart_upsell_bundle' ? 'plans' : 'accessory',
        quantity: upsellQty,
        value: addedValue(added, upsellQty)
      }, cartContext()));
    } catch (error) {
      await recoverCart();
    } finally {
      otherMutation = false;
      syncPendingUi();
      if (btn) {
        btn.classList.remove('btn--loading');
        btn.disabled = cartUnverified;
      }
    }
  }

  async function handleTierAdd(e) {
    var btn = e.target.closest('[data-tier-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    var variantId = btn.dataset.variantId;
    var qty = btn.dataset.qty || '1';
    if (!variantId) return;
    if (cartUnverified) { showDrawer('pricing_tier'); return; }
    if (isCartBusy()) return;
    otherMutation = true;
    verifiedCart = null;
    cartNotice = '';
    syncPendingUi();

    btn.classList.add('btn--loading');
    btn.disabled = true;
    var originalLabel = btn.textContent;
    btn.textContent = 'Adding…';

    try {
      var res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: parseInt(qty, 10), sections: SECTION_ID })
      });
      if (!res.ok) throw new Error('Add to cart failed');
      var added = await res.json();
      confirmAdded(added, parseInt(qty, 10));
      var cart = await fetchCart();
      cart.sections = added.sections;
      await renderCart(cart);
      showDrawer('pricing_tier', btn);
      var tierQty = parseInt(qty, 10) || 1;
      emit('cart_upsell_add', Object.assign({
        upsell: 'pricing_tier',
        quantity: tierQty,
        value: addedValue(added, tierQty)
      }, cartContext()));
    } catch (error) {
      await recoverCart();
      showDrawer('pricing_tier', btn);
    } finally {
      otherMutation = false;
      syncPendingUi();
      btn.classList.remove('btn--loading');
      btn.disabled = false;
      btn.textContent = originalLabel;
    }
  }

  function handleQtySelect(e) {
    var sel = e.target.closest('[data-cart-action="set-qty"]');
    if (!sel || sel.disabled) return;

    // "6+ · Contact sales" is a route, not a quantity: put the real quantity
    // back so the menu never displays a non-number, then open the fleet form.
    if (sel.value === 'contact') {
      var opt = sel.options[sel.selectedIndex];
      var url = opt && opt.dataset.salesUrl;
      sel.value = sel.dataset.qty;
      if (url) window.location.href = url;
      return;
    }

    var line = sel.dataset.lineIndex;
    var qty = parseInt(sel.value, 10);
    if (!line || isNaN(qty)) return;

    // data-qty still carries the quantity before this change: the server
    // stamps it and paintLineQty keeps it current on every optimistic paint.
    var prevQty = parseInt(sel.dataset.qty, 10);
    emit('cart_qty_change', Object.assign({
      line_index: parseInt(line, 10),
      from: isNaN(prevQty) ? null : prevQty,
      to: qty,
      item_id: lineItemId(sel.closest('.cart-line'))
    }, cartContext()));

    queueLineChange(line, qty);
  }

  function handleBodyClick(e) {
    const btn = e.target.closest('[data-cart-action]');
    if (!btn || btn.disabled) return;
    const action = btn.dataset.cartAction;
    const key    = btn.dataset.lineIndex;
    if (!key) return;

    // The quantity <select> also carries data-cart-action ("set-qty") and gets
    // a click on every open; Chrome on macOS delivers it AFTER the option is
    // chosen. That click used to fall through to the stepper branch below and
    // queue quantity minus 1, so picking 4 on a line at 3 landed back on 3
    // (Graham 2026-09-07, REQ-CART-PLAN-QTY-01, proved in Chrome). Only the
    // stepper buttons and remove may act on a click.
    if (action !== 'remove' && action !== 'increment' && action !== 'decrement') return;

    if (action === 'remove') {
      if (isCartBusy() || cartUnverified) return;
      var row = btn.closest('.cart-line');
      var rowSel = row && row.querySelector('.cart-line__qty-select');
      var removedQty = parseInt(rowSel ? rowSel.value : btn.dataset.qty, 10);
      emit('cart_line_remove', Object.assign({
        line_index: parseInt(key, 10),
        item_id: lineItemId(row),
        quantity: isNaN(removedQty) ? null : removedQty
      }, cartContext()));
      if (row) row.classList.add('is-removing');
      otherMutation = true;
      verifiedCart = null;
      cartNotice = '';
      syncPendingUi();
      updateLine(key, 0).catch(recoverCart).finally(function () {
        otherMutation = false;
        syncPendingUi();
      });
      return;
    }

    // Only the volume-upsell "+ Add trap" button reaches here now (the line's
    // own quantity is a <select>, handled in handleQtySelect). It sits outside
    // its .cart-line, so the server stamps data-qty on it and no /cart.js
    // pre-fetch is needed. A quantity already painted optimistically for this
    // line wins over the stamped one.
    var cur = pendingQty[key] ? pendingQty[key].qty : parseInt(btn.dataset.qty, 10);
    if (isNaN(cur)) {
      fetchCart().then(function (cart) {
        var item = (cart.items || [])[parseInt(key, 10) - 1];
        var q = item ? item.quantity : 0;
        queueLineChange(key, action === 'increment' ? q + 1 : Math.max(0, q - 1));
      });
      return;
    }
    queueLineChange(key, action === 'increment' ? cur + 1 : Math.max(0, cur - 1));
  }

  function findLine(index) {
    return drawerPanel ? drawerPanel.querySelector('.cart-line[data-line-index="' + index + '"]') : null;
  }

  function setFooterBusy(on) {
    var footer = drawerPanel && drawerPanel.querySelector('.cart-drawer__footer');
    if (footer) {
      footer.classList.toggle('is-busy', !!on);
      footer.setAttribute('aria-busy', String(!!on));
    }
    var checkout = drawerPanel && drawerPanel.querySelector('[data-ocu-track="cart_drawer_checkout"]');
    if (checkout) {
      var blocked = on || cartUnverified;
      checkout.setAttribute('aria-disabled', String(!!blocked));
      if (blocked) checkout.removeAttribute('href');
      else checkout.setAttribute('href', checkout.dataset.checkoutUrl || '/checkout');
      var label = checkout.querySelector('[data-checkout-label]');
      if (label) label.textContent = on ? 'Updating cart…' : 'Checkout';
    }
    var status = document.getElementById('CartDrawerStatus');
    if (status) {
      var message = on ? 'Updating your cart…' : cartNotice;
      status.hidden = !message;
      status.querySelector('[data-cart-status-message]').textContent = message;
      status.querySelector('[data-cart-status-recovery]').hidden = on || !cartUnverified;
    }
    if (drawerPanel) {
      drawerPanel.querySelectorAll('[data-cart-action], [data-cart-upsell-add] button').forEach(function (control) {
        // Keep only the active quantity editable so rapid selections coalesce.
        var ownQuantity = control.dataset.cartAction === 'set-qty' && pendingQty[control.dataset.lineIndex];
        control.disabled = cartUnverified || otherMutation || (on && !ownQuantity);
      });
    }
  }

  function isCartBusy() {
    return otherMutation || Object.keys(pendingQty).length > 0;
  }

  async function recoverCart() {
    try {
      await renderCart(await fetchCart());
      cartUnverified = false;
      cartNotice = 'We couldn’t confirm that change. Review your cart before trying again.';
    } catch (error) {
      cartUnverified = true;
      cartNotice = 'We couldn’t refresh your cart. Review the full cart before checkout.';
    }
  }

  // Paint an optimistic quantity on a line (qty text + stepper data-qty) and
  // dim the money that still has to come back from the server.
  function paintLineQty(key, qty) {
    var line = findLine(key);
    if (!line) return;
    var sel = line.querySelector('.cart-line__qty-select');
    if (sel) {
      // A quantity above the rendered range (cart edited elsewhere) needs an
      // option before it can be selected.
      if (!sel.querySelector('option[value="' + qty + '"]')) {
        var opt = document.createElement('option');
        opt.value = String(qty);
        opt.textContent = String(qty);
        // Keep the "Contact sales" route last.
        var route = sel.querySelector('option[value="contact"]');
        if (route) sel.insertBefore(opt, route); else sel.appendChild(opt);
      }
      sel.value = String(qty);
    }
    line.querySelectorAll('[data-cart-action][data-line-index]').forEach(function (b) { b.dataset.qty = String(qty); });
    line.classList.add('is-pending');
  }

  // After a server swap re-apply any quantities still in flight (the fresh HTML
  // carries the older value) and keep the footer dimmed until settled.
  // Clear first: a swap that lands while a change is still in flight re-adds
  // .is-pending, and without this reset the line's price stayed dimmed at 45%
  // opacity forever once the change settled (the final call only removed the
  // key, it never un-painted the line).
  function syncPendingUi() {
    if (drawerPanel) {
      drawerPanel.querySelectorAll('.cart-line.is-pending').forEach(function (line) {
        line.classList.remove('is-pending');
      });
      if (!otherMutation) drawerPanel.querySelectorAll('.cart-line.is-removing').forEach(function (line) {
        line.classList.remove('is-removing');
      });
    }
    var keys = Object.keys(pendingQty);
    keys.forEach(function (k) { paintLineQty(k, pendingQty[k].qty); });
    setFooterBusy(isCartBusy());
  }

  function queueLineChange(key, qty) {
    if (cartUnverified || otherMutation || (isCartBusy() && !pendingQty[key])) return;
    cartNotice = '';
    var entry = pendingQty[key] || (pendingQty[key] = { lineIndex: key, qty: qty, inflight: false, next: null });
    entry.qty = qty;
    paintLineQty(key, qty);
    setFooterBusy(true);
    if (entry.inflight) { entry.next = qty; return; }
    sendLineChange(key, qty);
  }

  async function sendLineChange(key, qty) {
    var entry = pendingQty[key];
    entry.inflight = true;
    entry.next = null;
    try {
      await updateLine(key, qty);
    } catch (error) {
      delete pendingQty[entry.lineIndex || key];
      otherMutation = true;
      verifiedCart = null;
      await recoverCart();
      otherMutation = false;
      syncPendingUi();
      return;
    }
    // A plan correction can remove an earlier row. Follow the surviving
    // line, or discard its queued edit if the cap removed this line entirely.
    key = entry.lineIndex;
    if (!key) return;
    entry.inflight = false;
    if (entry.next !== null && entry.next !== qty) {
      sendLineChange(key, entry.next);
    } else {
      delete pendingQty[key];
      syncPendingUi();
    }
  }

  async function updateLine(index, quantity) {
    verifiedCart = null;
    const res = await fetch('/cart/change.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body:    JSON.stringify({ line: parseInt(index, 10), quantity: quantity, sections: SECTION_ID })
    });
    if (!res.ok) throw new Error('Cart change failed');
    const cart = await res.json();
    await renderCart(cart);
  }

  function updateCartCount(count) {
    document.querySelectorAll('.cart-count, .ocutrap-header__cart-count').forEach(function (el) {
      el.textContent = String(count);
      el.style.display = count > 0 ? '' : 'none';
    });

    var cartBtn = document.querySelector('.ocutrap-header__cart[data-open-cart]');
    if (cartBtn) {
      var badge = cartBtn.querySelector('.ocutrap-header__cart-count');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'cart-badge ocutrap-header__cart-count';
          badge.setAttribute('data-cart-badge', '');
          cartBtn.appendChild(badge);
        }
        badge.textContent = String(count);
        badge.style.display = '';
      } else if (badge) {
        badge.remove();
      }
      cartBtn.setAttribute('aria-label', 'View cart (' + count + ')');
    }
  }

  function updateDrawerTitleBadge(count) {
    var title = drawerPanel && drawerPanel.querySelector('.cart-drawer__title');
    if (!title) return;
    var badge = title.querySelector('#CartDrawerCountBadge');
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.id = 'CartDrawerCountBadge';
        badge.className = 'badge badge--green';
        badge.style.marginLeft = '8px';
        title.appendChild(badge);
      }
      badge.textContent = String(count);
      badge.style.display = '';
    } else if (badge) {
      badge.remove();
    }
  }

  function swapSectionFromDoc(doc, selector) {
    if (!drawerPanel) return;
    var incoming = doc.querySelector(selector);
    var current = drawerPanel.querySelector(selector);

    if (incoming && current) {
      current.replaceWith(document.importNode(incoming, true));
      return;
    }

    if (incoming && !current) {
      var items = drawerPanel.querySelector('.cart-drawer__items');
      if (items) {
        items.after(document.importNode(incoming, true));
      } else if (cartBody) {
        cartBody.appendChild(document.importNode(incoming, true));
      }
      return;
    }

    if (!incoming && current) {
      current.remove();
    }
  }

  function applyDrawerHtml(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    if (!doc.getElementById('CartDrawer') || !doc.querySelector('.cart-drawer__items') || !doc.querySelector('.cart-drawer__footer')) {
      throw new Error('Incomplete cart drawer response');
    }
    // A swap replaces the focused control (the quantity select just changed,
    // an upsell button just pressed), which drops focus to <body>. Put it back
    // on the re-rendered control with the same id, else on the close button.
    var active = document.activeElement;
    var hadFocus = !!(drawerPanel && active && drawerPanel.contains(active));
    var activeId = hadFocus ? active.id : '';
    SECTION_SWAP_SELECTORS.forEach(function (sel) {
      swapSectionFromDoc(doc, sel);
    });
    cartBody = document.getElementById('CartDrawerBody');
    syncPendingUi();
    if (hadFocus && isOpen() && !drawerPanel.contains(document.activeElement)) {
      var again = activeId && document.getElementById(activeId);
      focusEl(again && !again.disabled ? again : closeBtn);
    }
  }

  // Fallback when a Cart API response carried no section HTML.
  async function refreshDrawerSections() {
    var res = await fetch('/cart?section_id=' + SECTION_ID);
    if (!res.ok) throw new Error('Failed to refresh cart drawer section');
    applyDrawerHtml(await res.text());
  }

  // A prepaid Year-1 plan is one-per-trap: the plan line may never exceed the
  // trap quantity. Runs on every cart render, so lowering (or removing) traps
  // silently drags the plan line down with them, and bumping the plan line
  // above the trap count snaps it straight back.
  async function enforcePlanCap(cart) {
    var trapHandle = drawer && drawer.dataset.trapHandle;
    var planHandle = drawer && drawer.dataset.planHandle;
    if (!trapHandle || !planHandle) return cart;

    var trapQty = 0;
    var planQty = 0;
    var planLines = [];
    (cart.items || []).forEach(function (item, i) {
      if (item.handle === trapHandle) trapQty += item.quantity;
      if (item.handle === planHandle) {
        planQty += item.quantity;
        planLines.push({ item: item, index: i + 1 });
      }
    });
    if (!planLines.length || planQty <= trapQty) return cart;

    // One variant can occupy distinct lines (different properties/discounts).
    // Allocate the available traps in cart order; never increase a plan line.
    var room = trapQty;
    var updates = {};
    var removed = [];
    planLines.forEach(function (line) {
      var quantity = Math.min(line.item.quantity, room);
      room -= quantity;
      if (quantity !== line.item.quantity) updates[line.item.key] = quantity;
      if (!quantity) removed.push(line.index);
    });
    var single = planLines.length === 1;
    var res = await fetch(single ? '/cart/change.js' : '/cart/update.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(single
        ? { line: planLines[0].index, quantity: trapQty, sections: SECTION_ID }
        : { updates: updates, sections: SECTION_ID })
    });
    if (!res.ok) throw new Error('Failed to match plan quantity to traps');
    var corrected = await res.json();
    if (removed.length) {
      var remapped = {};
      Object.keys(pendingQty).forEach(function (key) {
        var index = parseInt(key, 10);
        var entry = pendingQty[key];
        if (removed.indexOf(index) !== -1) {
          entry.lineIndex = null;
          return;
        }
        var before = removed.filter(function (n) { return n < index; }).length;
        entry.lineIndex = String(index - before);
        remapped[entry.lineIndex] = entry;
      });
      pendingQty = remapped;
    }
    return corrected;
  }

  // The plan page stays mounted when the drawer changes. Keep its buying
  // controls consistent with the verified cart, including distinct plan lines.
  function updatePlanProduct(cart) {
    var product = document.querySelector('[data-plan-product]');
    if (!product) return;
    var trapQty = 0, planQty = 0;
    (cart.items || []).forEach(function (item) {
      if (item.handle === drawer.dataset.trapHandle) trapQty += item.quantity;
      if (item.handle === drawer.dataset.planHandle) planQty += item.quantity;
    });
    var room = Math.max(0, trapQty - planQty);
    var formRegion = product.querySelector('[data-plan-form]');
    var quantity = product.querySelector('#PsQuantity');
    if (!formRegion || !quantity) return;
    var wasHidden = formRegion.hidden;
    var selected = wasHidden ? room : Math.min(parseInt(quantity.value, 10) || 1, room);
    quantity.replaceChildren();
    for (var n = 1; n <= room; n++) {
      var option = document.createElement('option');
      option.value = String(n);
      option.textContent = String(n);
      option.defaultSelected = n === selected;
      quantity.appendChild(option);
    }
    formRegion.hidden = room < 1;
    product.querySelector('[data-plan-route]').hidden = room > 0;
    product.querySelector('[data-plan-covered]').hidden = trapQty < 1;
    product.querySelector('[data-plan-empty]').hidden = trapQty > 0;
    product.querySelector('[data-plan-hint]').textContent = 'One per trap in your cart, up to ' + room;
  }

  async function renderCart(cart) {
    cart = await enforcePlanCap(cart);
    // The footer (subtotal / discount rows / total) is server-rendered: use the
    // section HTML the Cart API response carried, else fetch it. No client-side
    // money formatting (the old Intl override rounded to whole dollars and hid
    // percentage-discount cents).
    var html = cart && cart.sections && cart.sections[SECTION_ID];
    if (html) applyDrawerHtml(html); else await refreshDrawerSections();
    updateCartCount(cart.item_count);
    updateDrawerTitleBadge(cart.item_count);
    updatePlanProduct(cart);
    verifiedCart = cart;
  }

  document.addEventListener('DOMContentLoaded', init);

  // The full cart page edits quantities through Horizon's own component, not
  // this drawer. Its changes dispatch the standard 'shopify:cart:lines-update'
  // event (bubbles to document); re-check the cap after each burst and reload
  // the page if a clamp actually changed the cart, so the server-rendered cart
  // page shows the corrected plan line. The clamp's own /cart/change.js fetch
  // dispatches no event, so this cannot loop.
  var planCapTimer = null;
  var planCapPending = Promise.resolve();
  document.addEventListener('shopify:cart:lines-update', function (e) {
    if (window.location.pathname.indexOf('/cart') !== 0) return;
    // The event fires when the change starts; e.promise settles when it lands.
    if (e.promise && typeof e.promise.then === 'function') {
      planCapPending = e.promise.catch(function () {});
    }
    clearTimeout(planCapTimer);
    planCapTimer = setTimeout(function () {
      planCapPending.then(fetchCart).then(function (cart) {
        return enforcePlanCap(cart).then(function (after) {
          updateCartCount(after.item_count);
          updateDrawerTitleBadge(after.item_count);
          if (after !== cart) window.location.reload();
        });
      }).catch(function () { /* cart fetch failed; leave the page alone */ });
    }, 600);
  });

  // Same check on cart-page loads, for mismatches created elsewhere (another
  // tab or device) before this script was around to clamp them.
  document.addEventListener('DOMContentLoaded', function () {
    if (window.location.pathname.indexOf('/cart') !== 0) return;
    fetchCart().then(function (cart) {
      return enforcePlanCap(cart).then(function (after) {
        updateCartCount(after.item_count);
        updateDrawerTitleBadge(after.item_count);
        if (after !== cart) window.location.reload();
      });
    }).catch(function () { /* cart fetch failed; leave the page alone */ });
  });
})();
